import { Injectable, inject } from '@angular/core';
import { Order, QueryConfig, QueryEntity } from '@datorama/akita';
import { Contact, ContactStateType, UserAccessRoleType } from '@models/profile/model';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs';

import { ContactState, ContactStore } from './contact.store';

@Injectable({
  providedIn: 'root',
})
@QueryConfig({
  sortBy: 'created',
  sortByOrder: Order.DESC,
})
export class ContactQuery extends QueryEntity<ContactState> {
  protected store: ContactStore;

  constructor() {
    const store = inject(ContactStore);

    super(store);

    this.store = store;
  }

  public selectAllWhenLoaded(): Observable<Contact[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.selectAll())
    );
  }

  public getActiveId(): string {
    return super.getActiveId() as string;
  }

  public selectActiveWhenLoaded(): Observable<Contact> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.selectActive())
    );
  }

  public selectPrimaryContacts(): Observable<Contact[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.selectAll({
          filterBy: (contact) => contact.primaryContact,
        })
      )
    );
  }

  public selectAdminContacts(): Observable<Contact[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.selectAll({
          filterBy: (contact) => contact.userAccessRoles.includes(UserAccessRoleType.CU1),
        })
      )
    );
  }

  public selectByEmail(email: string): Observable<Contact> {
    return this.selectAllWhenLoaded().pipe(
      map((contacts) => {
        return contacts.find((contact) => contact.email.toLowerCase() === email.toLowerCase());
      })
    );
  }

  public selectActiveContacts(): Observable<Contact[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.selectAll({
          filterBy: (contact) => contact.currentState === ContactStateType.Active,
        })
      )
    );
  }

  public selectPendingApprovalContacts(): Observable<Contact[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.selectAll({
          filterBy: (contact) => contact.currentState === ContactStateType.PendingApproval,
        })
      )
    );
  }

  public selectById(id: string): Observable<Contact> {
    return this.selectAllWhenLoaded().pipe(
      map((contacts) => {
        return contacts.find((contact) => contact.id === id);
      })
    );
  }
}
